// Typed client for contracts/openapi.json. Branch on PROBLEM types, never on
// a problem's title.

export const PROBLEM = {
  unauthenticated: 'https://archive.example/problems/unauthenticated',
  quotaExceeded: 'https://archive.example/problems/quota-exceeded',
  grantExpired: 'https://archive.example/problems/grant-expired',
  commitSizeMismatch: 'https://archive.example/problems/commit-size-mismatch',
} as const;

export interface Problem {
  type: string; title: string; status: number; detail?: string;
  shortfallBytes?: number; bytesAvailable?: number;
  mismatches?: Array<{ packId: string; declared: number; actual: number }>;
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) { super(problem.title); }
  is(type: string) { return this.problem.type === type; }
}

export interface Quota { bytesUsed: number; bytesLimit: number; memberCount: number }
export interface Profile {
  accountId: string; displayName: string | null; email: string | null;
  avatarUrl: string | null; locale: string | null;
  homeRegion: string; status: string; createdAt: string;
  /** True when the service can decrypt this archive — see D-011. */
  keyHeldByService: boolean;
  /** Whether this account has turned on the features that read messages. */
  aiEnabled: boolean;
  identities: Array<{ provider: string; email: string | null; emailVerified: boolean; lastSeenAt: string }>;
  passkeyCount: number;
}
/** A turn with the model, in the shape Vertex speaks. */
export interface AiTurn {
  role: 'user' | 'model';
  parts: Array<Record<string, unknown>>;
}

/** One entry in the log D-013 promises. Never content — see 0006_ai_reads.sql. */
export interface AiRead {
  readId: string; chatId?: string; feature: string; model: string;
  messageCount: number; inputTokens?: number; outputTokens?: number;
  outcome: 'ok' | 'error' | 'refused'; at: string;
}
export interface Device {
  deviceId: string; platform: string; label: string | null;
  createdAt: string; lastSeenAt: string;
}

export interface WrappedKey {
  /** 'escrow' is the service holding it — see D-011. */
  method: 'passkey' | 'recovery' | 'escrow'; version: number;
  credentialSalt: string; wrappedKey: string;
}
export interface Account {
  accountId: string; homeRegion: string; status: string;
  displayName?: string | null; email?: string | null; avatarUrl?: string | null;
  quota: Quota; credentials?: WrappedKey[];
}
export interface Chat {
  chatId: string; titleCiphertext: string; avatarCiphertext?: string;
  directoryCid?: string;
  messageCount: number; lastMessageAt?: string;
}
export interface ManifestMember {
  contentId: string; packIndex: number; payloadOffset: number; payloadLength: number;
  plainLength: number; frameSize: number; kind: number;
}
export interface MediaLocation {
  url: string; packOffset: number; payloadLength: number;
  plainLength: number; frameSize: number; kind: number; expiresAt: number;
}
const locations = new Map<string, MediaLocation>();

export interface UploadGrant {
  packId: string; packIndex: number; url: string; expiresAt: string;
  requiredHeaders: Record<string, string>;
}

let token: string | null = sessionStorage.getItem('wa.token');
let refreshToken: string | null = localStorage.getItem('wa.refresh');
/**
 * Whether this browser has anything to come back with.
 *
 * It asked `token`, which lives in sessionStorage and dies with the tab — so
 * closing the tab logged people out while a refresh token good for thirty days
 * sat untouched in localStorage beside it. The refresh token is the session;
 * the access token is a ten-minute detail.
 */
export const hasSession = () => token !== null || refreshToken !== null;
export const clearSession = () => {
  token = null; refreshToken = null;
  sessionStorage.removeItem('wa.token');
  localStorage.removeItem('wa.refresh');
};

/**
 * Access tokens live ten minutes. Without this every session simply stopped
 * working after that, with "No valid session" and no way back.
 *
 * One refresh at a time: a page that fires six requests as a token expires
 * must not rotate six times, because rotation is single-use and the second
 * rotation would look like a replay and kill the family.
 */
let refreshing: Promise<boolean> | null = null;

async function refresh(): Promise<boolean> {
  if (!refreshToken) return false;
  refreshing ??= (async () => {
    try {
      const res = await fetch('/v1/auth/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      // Só um "não" do servidor encerra a sessão. Um 404, um 502 ou uma
      // queda de rede dizem que a pergunta não chegou, e apagar a sessão por
      // isso derruba um token de acesso que ainda funciona — foi assim que um
      // refresh falho no começo de uma importação a fazia começar sem token.
      if (res.status === 400 || res.status === 401 || res.status === 403) {
        clearSession();
        return false;
      }
      if (!res.ok) return false;
      const r = (await res.json()) as { accessToken: string; refreshToken: string };
      token = r.accessToken;
      refreshToken = r.refreshToken;
      sessionStorage.setItem('wa.token', token);
      localStorage.setItem('wa.refresh', refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function once(method: string, path: string, body?: unknown) {
  return fetch(path, {
    method,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res = await once(method, path, body);
  if (res.status === 401 && (await refresh())) res = await once(method, path, body);

  const text = await res.text();
  const parsed = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(parsed as Problem);
  return parsed as T;
}

/** O que o servidor oferece como porta de entrada. Ver lib/authcache. */
export interface AuthConfig {
  passkeysAvailable: boolean; rpId: string | null; reason: string | null;
  googleAvailable: boolean;
  escrowAvailable: boolean;
  appleAvailable?: boolean;
  firebase: { apiKey: string; authDomain: string; projectId: string; appId: string } | null;
}

export const api = {
  async signIn(idpToken: string, credentials: unknown[]) {
    const r = await call<{
      accessToken: string; refreshToken: string; account: Account; isNewAccount: boolean;
    }>('POST', '/v1/session', { idpToken, platform: 'web', credentials });
    token = r.accessToken;
    refreshToken = r.refreshToken;
    sessionStorage.setItem('wa.token', token);
    localStorage.setItem('wa.refresh', refreshToken);
    return r;
  },
  account: () => call<Account>('GET', '/v1/account'),
  renameChat: (chatId: string, titleCiphertext: string) =>
    call<{ chatId: string }>('PATCH', `/v1/chats/${chatId}`, { titleCiphertext }),
  deleteChat: (chatId: string) =>
    call<{ chatId: string; membersRemoved: number; packsRemoved: number;
           freedBytes: number; spacePending: boolean }>('DELETE', `/v1/chats/${chatId}`),
  /** Best-effort and never awaited by anything the person is watching. */
  report: (what: Record<string, unknown>) =>
    call<void>('POST', '/v1/diagnostics', what).catch(() => undefined),
  reclaim: () =>
    call<{ freedBytes: number; membersRemoved: number; packsRemoved: number;
           spacePending: boolean }>('POST', '/v1/account/reclaim'),
  registerChatMembers: (chatId: string, memberIds: string[]) =>
    call<{ chatId: string; members: number }>('PUT', `/v1/chats/${chatId}/members`, { memberIds }),
  setChatAvatar: (chatId: string, avatarCiphertext: string | null) =>
    call<{ chatId: string }>('PATCH', `/v1/chats/${chatId}`, { avatarCiphertext }),
  chats: () => call<{ chats: Chat[] }>('GET', '/v1/chats'),
  openImport: (source: string) => call<{ importId: string }>('POST', '/v1/imports', { source }),
  manifest: (importId: string, members: ManifestMember[], packs: { declaredBytes: number }[]) =>
    call<{ newMembers: string[]; alreadyHeld: number; grants: UploadGrant[]; quota: Quota }>(
      'POST', `/v1/imports/${importId}/manifest`, { members, packs }),
  commit: (importId: string, body: unknown) =>
    call<{ bytesCommitted: number; quota: Quota }>('POST', `/v1/imports/${importId}/commit`, body),

  /**
   * Where a member lives. Cached for the life of the signed URL, because a
   * ranged read touches this once per frame otherwise.
   */
  async mediaLocation(contentId: string): Promise<MediaLocation> {
    const hit = locations.get(contentId);
    if (hit && hit.expiresAt > Date.now() + 15_000) return hit;
    const r = await call<Omit<MediaLocation, 'expiresAt'> & { expiresIn: number }>(
      'GET', `/v1/media/${contentId}`);
    const loc = { ...r, expiresAt: Date.now() + r.expiresIn * 1000 };
    locations.set(contentId, loc);
    return loc;
  },

  /** One ranged read of a member's *encrypted* payload, translated into a
   *  range over the pack that holds it. */
  async mediaRange(contentId: string, from: number, to: number): Promise<Uint8Array> {
    const loc = await api.mediaLocation(contentId);
    const start = loc.packOffset + from;
    const end = loc.packOffset + Math.min(to, loc.payloadLength - 1);
    const res = await fetch(loc.url, { headers: { range: `bytes=${start}-${end}` } });
    if (!res.ok && res.status !== 206) throw new Error(`media ${contentId}: ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  },

  currentToken: () => token,

  /**
   * A token good for the next call, refreshing if the one in hand has expired.
   *
   * The ingest worker cannot reach the refresh machinery — it has its own fetch
   * and its own copy of the token — so it asks for this instead. An import can
   * run for minutes and an access token lives ten, which made a long upload a
   * guaranteed 401 partway through.
   */
  freshToken: async (): Promise<string | null> => {
    // O que importa é devolver algo utilizável: se a renovação não vingou mas
    // o token atual continua de pé, ele serve, e quem chamou não precisa
    // saber a diferença.
    await refresh();
    return token;
  },

  /** Raw POST, for the WebAuthn ceremonies in @wa/auth. */
  post: (path: string, body: unknown) => call<Record<string, unknown>>('POST', path, body),

  /** Adopt the session a ceremony just established. */
  adoptSession(accessToken: string, refresh: string) {
    token = accessToken;
    refreshToken = refresh;
    sessionStorage.setItem('wa.token', token);
    localStorage.setItem('wa.refresh', refreshToken);
  },

  authConfig: () => call<AuthConfig>('GET', '/v1/auth/config'),

  profile: () => call<Profile>('GET', '/v1/profile'),
  setDisplayName: (displayName: string) => call<Profile>('PATCH', '/v1/profile', { displayName }),
  setAiEnabled: (aiEnabled: boolean) => call<Profile>('PATCH', '/v1/profile', { aiEnabled }),
  aiReads: () => call<{ reads: AiRead[] }>('GET', '/v1/ai/reads'),
  /** One round of the archive-wide conversation. The loop lives in the client. */
  ask: (turns: AiTurn[], tools: unknown[], readCount: number) =>
    call<{ turn: AiTurn; readId: string }>('POST', '/v1/ai/ask', { turns, tools, readCount }),

  devices: () => call<{ devices: Device[] }>('GET', '/v1/devices'),
  revokeDevice: (deviceId: string) => call<void>('DELETE', `/v1/devices/${deviceId}`),
  linkIdentity: (idpToken: string) => call<{ linked: boolean }>('POST', '/v1/auth/link', { idpToken }),

  /** Hand the service the account key to hold. Once only; a second call is
   *  refused rather than overwriting what opens the archive. */
  holdKey: (accountKey: string) => call<{ held: boolean }>('POST', '/v1/keys/escrow', { accountKey }),
  heldKey: () => call<{ accountKey: string }>('POST', '/v1/keys/escrow/open', {}),
  dropHeldKey: () => call<{ held: boolean }>('DELETE', '/v1/keys/escrow'),

  /** Crypto-shred: the keys go first, so the archive is unreadable from that
   *  instant whatever happens to the bytes afterwards. Irreversible. */
  shredAccount: () => call<{ shreddedAt: string }>('DELETE', '/v1/account', { confirm: 'DELETE' }),

  signInWithIdp: (idpToken: string) => call<{
    accessToken: string; refreshToken: string; account: Account;
    isNewAccount: boolean; needsKeyCredential: boolean;
  }>('POST', '/v1/session', { idpToken, platform: 'web' }),
};
